/**
 * ¿LA PESTAÑA ABIERTA SIGUE SIENDO LA VERSIÓN QUE ESTÁ SERVIDA?
 *
 * Por qué existe. La aplicación no carga pedazos de JavaScript a demanda: todo viene en
 * la carga inicial. Eso tiene una cara buena —reemplazar el contenedor durante un
 * despliegue **no rompe** una pestaña abierta, el que está trabajando sigue trabajando—
 * y una mala: esa pestaña se queda con el código que cargó **para siempre**, hasta que
 * alguien la recargue.
 *
 * Casi siempre se cura solo: durante un despliegue el motor deja de contestar, aparece
 * la pantalla de "estamos actualizando" y al volver el motor recarga. Pero si en esa
 * ventana el usuario no pidió NADA al servidor —estaba leyendo, o con la pestaña de
 * fondo—, la pantalla nunca aparece y la pestaña queda vieja sin que nada lo note. Con
 * los días eso es una bomba de tiempo: código viejo hablándole a un motor nuevo.
 *
 * Cómo se detecta, sin agregar plomería al build: Vite le pone un hash en el nombre a
 * cada archivo de JavaScript y CSS, así que **el conjunto de nombres ES la versión**. Se
 * compara el que tiene la página cargada contra el que sirve el servidor ahora.
 *
 * Nada de esto inventa un número de versión ni depende de que alguien pase una variable
 * al compilar: si esa variable se olvidara, el mecanismo se apagaría en silencio. La
 * huella sale del artefacto mismo.
 */

/** Archivos cuyo nombre Vite versiona con un hash. El favicon y demás quedan fuera. */
const VERSIONADOS = /\.(?:js|mjs|css)(?:\?|$)/i;

/**
 * Une una lista de direcciones en una sola huella comparable. Ordena a propósito: el
 * ORDEN en que el navegador o el HTML las declare no es parte de la versión, y si lo
 * fuera daría falsos positivos.
 */
export function huellaDeUrls(urls: readonly string[]): string {
  const versionados = urls
    .map((u) => u.trim())
    .filter((u) => u.length > 0 && VERSIONADOS.test(u))
    // Solo el nombre del archivo: la pestaña ve rutas absolutas del navegador
    // (https://host/assets/x.js) y el HTML servido las trae relativas (/assets/x.js).
    .map((u) => u.split('/').pop() ?? u)
    .sort();
  return Array.from(new Set(versionados)).join('|');
}

/** Direcciones de assets que declara un HTML servido (el `index.html` del servidor). */
export function urlsDeAssetsDelHtml(html: string): string[] {
  const out: string[] = [];
  const re = /(?:src|href)\s*=\s*["']([^"']+)["']/gi;
  let m: RegExpExecArray | null;
  while ((m = re.exec(html))) out.push(m[1]!);
  return out;
}

/** Direcciones de assets que tiene cargada la página en este momento. */
export function urlsDeAssetsDelDocumento(doc: Document): string[] {
  const nodos = doc.querySelectorAll<HTMLElement>('script[src], link[href]');
  return Array.from(nodos).map(
    (n) => n.getAttribute('src') ?? n.getAttribute('href') ?? '',
  );
}

export type Veredicto = 'igual' | 'version-nueva' | 'no-se-puede-saber';

/**
 * Compara la versión cargada contra la servida.
 *
 * Falla CERRADO a propósito: ante cualquier duda devuelve `no-se-puede-saber` y no se
 * recarga nada. Recargar de más le interrumpe el trabajo a alguien; no recargar solo
 * posterga. En dev, por ejemplo, el HTML apunta a `/src/main.tsx` —sin hash— y la huella
 * queda vacía: ahí el mecanismo tiene que quedarse quieto, no recargar en bucle.
 */
export function compararVersion(cargada: string, servida: string): Veredicto {
  if (!cargada || !servida) return 'no-se-puede-saber';
  return cargada === servida ? 'igual' : 'version-nueva';
}

/**
 * ¿Corresponde recargar?
 *
 * `yaRecargadaHacia` es la huella a la que se recargó la última vez, guardada por la
 * propia pestaña. Sin esto, si el servidor contestara algo distinto de lo que después
 * entrega —una cache intermedia, un balanceador con dos versiones arriba— la pestaña
 * entraría en un bucle de recargas y le arruinaría el día al usuario.
 */
export function debeRecargar(args: {
  cargada: string;
  servida: string;
  yaRecargadaHacia: string | null;
}): boolean {
  if (compararVersion(args.cargada, args.servida) !== 'version-nueva') return false;
  return args.servida !== args.yaRecargadaHacia;
}
